<template>
  <div>
    <EmptyMessage v-if="noSelectedTokenIds"> Select a word to show its concepts </EmptyMessage>
    <template v-else>
      <LoadingSpinner v-if="$apollo?.loading" />
      <EmptyMessage v-if="concepts?.length === 0">
        No concepts found for the current selection
      </EmptyMessage>
      <ConceptDetail
        v-else-if="selectedConceptUri"
        :key="selectedConceptUri"
        :conceptUri="selectedConceptUri"
        :hasMore="concepts?.length > 1"
        @select-concept-uri="uri => (selectedConceptUri = uri)"
      />
      <ConceptList
        v-else-if="concepts?.length"
        :concepts="concepts"
        @select-concept-uri="uri => (selectedConceptUri = uri)"
      />
    </template>
  </div>
</template>

<script>
  import gql from 'graphql-tag';
  import mixins from '@/mixins';
  import LoadingSpinner from '@/symphony/widgets/components/LoadingSpinner.vue';
  import EmptyMessage from '@/symphony/widgets/components/EmptyMessage.vue';
  import { DEFAULT_GROUP_KEY } from '@/store/constants';
  import ConceptList from './ConceptList.vue';
  import ConceptDetail from './ConceptDetail.vue';

  export default {
    name: 'ConceptsWidget',
    components: {
      LoadingSpinner,
      EmptyMessage,
      ConceptList,
      ConceptDetail,
    },
    mixins: [mixins.InputDataMixin],
    data() {
      return {
        concepts: [],
        selectedConceptUri: null,
      };
    },
    props: {
      info: Object,
      doric: {
        inputs: {
          label: 'Concepts',
          selectedTokenIds: {
            value: 'selectedTokenIds',
            groupKey: DEFAULT_GROUP_KEY,
          },
        },
        outputs: {},
      },
    },
    apollo: {
      concepts: {
        query: gql`
          query WordTokens($filters: WordTokenFilter) {
            wordTokens(filters: $filters) {
              id
              concepts {
                uri
                data
                label
              }
            }
          }
        `,
        variables() {
          return {
            filters: {
              id: {
                inList: this.parsedSelectedTokenIds,
              },
            },
          };
        },
        skip() {
          return this.noSelectedTokenIds;
        },
        update(data) {
          // Flatten all concepts from selected tokens and deduplicate
          const tokenConcepts = data.wordTokens
            .flatMap(token => token.concepts)
            .map(concept => ({
              ...concept,
              code: concept.data?.code || undefined,
            }));
          return [...new Set(tokenConcepts)];
        },
      },
    },
    computed: {
      parsedSelectedTokenIds() {
        try {
          const value = this.selectedTokenIds;
          if (!value) {
            return [];
          }
          const parsed = JSON.parse(value);
          return Array.isArray(parsed) ? parsed : [];
        } catch {
          return [];
        }
      },
      noSelectedTokenIds() {
        return (
          !Array.isArray(this.parsedSelectedTokenIds) || this.parsedSelectedTokenIds.length === 0
        );
      },
    },
    watch: {
      selectedTokenIds() {
        this.selectedConceptUri = null;
      },
      concepts(newValue) {
        this.autoSelectSelectedConceptUri(newValue);
      },
    },
    methods: {
      autoSelectSelectedConceptUri(concepts) {
        if (concepts?.length === 1) {
          // If there is only one concept, select it automatically
          // so the user doesn't have to take an extra action to view
          // concept data.
          this.selectedConceptUri = concepts[0].uri;
        }
      },
    },
  };
</script>
